import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { dataHora } from '../util/formato';
import { Alerta, Botao } from './base';
import { AreaDeTexto } from './campos';

export interface Atualizacao {
  readonly id: string;
  readonly tipo: string;
  readonly descricao: string;
  readonly criadoEm: string;
  readonly autor: { nome: string } | null;
  readonly autorExterno: string | null;
}

/**
 * Atualizações escritas pela equipe, na ficha do caso.
 *
 * O "Histórico do caso" logo ao lado mostra o que o SISTEMA registrou — baixa,
 * mudança de fase, documento anexado. Aqui fica o que só existe na cabeça de quem
 * atendeu: a ligação que caiu, a promessa de pagar na sexta, o telefone que já é de
 * outra pessoa. Sem este campo, isso continua no WhatsApp da colaboradora e se
 * perde quando ela sai de férias.
 *
 * A lista filtra `NOTA` justamente por isso: misturar as duas coisas devolveria o
 * histórico completo, que já está na coluna principal, e a nota escrita à mão se
 * perderia no meio dos lançamentos automáticos.
 *
 * Nada aqui se edita nem se apaga — a linha do tempo do caso só cresce, e é isso
 * que permite reconstituir meses depois por que o caso foi para o judicial.
 */
export function AtualizacoesDoCaso({
  devedorId,
  interacoes,
}: {
  devedorId: string;
  interacoes: readonly Atualizacao[];
}) {
  const clienteConsulta = useQueryClient();
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const notas = interacoes.filter((i) => i.tipo === 'NOTA');

  const registrar = useMutation({
    mutationFn: () => api.criar(`/devedores/${devedorId}/notas`, { texto: texto.trim() }),
    onSuccess: () => {
      setTexto('');
      setErro(null);
      void clienteConsulta.invalidateQueries({ queryKey: ['devedor', devedorId] });
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível registrar a atualização.'),
  });

  return (
    <>
      <div className="flex items-center justify-between gap-3 p-5 pb-3">
        <h2 className="text-lg text-[color:var(--texto)]">
          Atualizações
          <span className="ml-3 text-xs font-normal text-[color:var(--texto-tenue)]">
            {notas.length}
          </span>
        </h2>
        <span className="shrink-0 text-xs text-[color:var(--texto-tenue)]">
          Só notas da equipe
        </span>
      </div>

      <div className="space-y-3 px-5 pb-4">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        <AreaDeTexto
          valor={texto}
          aoMudar={setTexto}
          linhas={3}
          placeholder="Escreva uma atualização…"
        />

        <div className="flex justify-end">
          <Botao
            tipo="primario"
            onClick={() => registrar.mutate()}
            desabilitado={registrar.isPending || texto.trim().length === 0}
            className="px-3 py-1.5 text-sm"
          >
            {registrar.isPending ? 'Registrando…' : 'Registrar'}
          </Botao>
        </div>
      </div>

      {notas.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-[color:var(--texto-tenue)]">
          Nenhuma atualização escrita ainda. O que o sistema registrou sozinho está no
          histórico do caso.
        </p>
      ) : (
        <ol className="max-h-[22rem] overflow-y-auto border-t">
          {notas.map((n) => (
            <li key={n.id} className="border-b px-5 py-3 last:border-0">
              <p className="text-[0.6875rem] text-[color:var(--texto-tenue)]">
                {dataHora(n.criadoEm)} · {n.autor?.nome ?? n.autorExterno ?? 'Sistema'}
              </p>
              {/* `whitespace-pre-line`: quem escreve uma atualização quebra linha, e
                  sem isto o texto todo viraria um parágrafo só. */}
              <p className="mt-0.5 whitespace-pre-line text-sm text-[color:var(--texto)]">
                {n.descricao}
              </p>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}
